import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import Image from 'next/image'
import {
  LivePlayer,
  OnNowOnNext,
  Spinner,
  LoginDialog,
} from '..'
import ScrollerMenu from '../ScrollerMenu'
import styles from './styles'
import { OUT_OF_REGION_LOGGED_IN_GROUPTOKEN } from '../../constants'
import { ERROR_OBJECT } from '../../utils/types'
import errorRedirect from '../../utils/errorRedirect'
import OutOfRegion from '../OutOfRegion'
import LiveTabs from '../LiveTabs'
import ConcurrentStreamNotice from '../ConcurrentStreamNotice'
import PaywallNotice from '../PaywallNotice'
import { checkConcurrentStream, clearConcurrentStream } from '../../actions/concurrentStreamActions'
import paywallBgImage from '../PaywallNotice/image.png'

const Live = ({
  error,
  channel,
  currentUrl,
  currentDomain,
  userAgent,
  streamMenuItems,
  concurrencyCapKillswitch,
  programmeOrChannelGuid,
  groupToken,
  logo,
  loginEnabled,
  promoted,
  loginChecked,
  strapiData,
  VIPUser,
  menuTitle,
  onNowNextKillSwitch,
  hasSchedule,
  scheduleAssets,
  sportsExclusive,
}) => {
  const showPlayerError = error && error.code === 'NOT_LOGGED_IN'
  const dispatch = useDispatch()
  const users = useSelector(state => state.user)
  const psEnabled = useSelector(state => state.featureFlags?.psEnabled)
  // eslint-disable-next-line max-len
  const { showModal: hasConcurrentStream, loading: concurrentLoading } = useSelector(state => state.concurrentStream)

  // Some errors we want to be handled by the player component
  const renderError = [404, 500].includes(error?.httpStatus)

  if (error && renderError) {
    error.currentUrl = currentUrl // eslint-disable-line no-param-reassign
  }

  const [mobileView, setMobileView] = useState(false)
  const [bannerData, setBannerData] = useState(null)

  useEffect(() => {
    if (window.innerWidth >= 767) {
      setMobileView(false)
    } else {
      setMobileView(true)
    }
  }, [])

  useEffect(() => {
    const genericContent = strapiData?.data?.filter(item => item?.attributes?.guid === 'generic')
    if (genericContent?.length > 0) {
      setBannerData({
        content: genericContent[0]?.attributes,
        articleNotFound: false,
      })
    }
    const vipContent = strapiData?.data?.filter(item => item?.attributes?.guid === 'player-vip')
    const nonVipContent = strapiData?.data?.filter(item => item?.attributes?.guid === 'non-vip')
    if (nonVipContent?.length > 0 && VIPUser === false && users.currentlySubscribed === false) {
      setBannerData({
        content: nonVipContent[0]?.attributes,
        articleNotFound: false,
      })
    }
    if (vipContent?.length > 0 && VIPUser === true && users.currentlySubscribed === false) {
      setBannerData({
        content: vipContent[0]?.attributes,
        articleNotFound: false,
      })
    }
    const plusContent = strapiData?.data?.filter(item => item?.attributes?.guid === 'player-plus')
    const nonPlusContent = strapiData?.data?.filter(item => item?.attributes?.guid === 'non-plus')
    if (nonPlusContent?.length > 0 && users.currentlySubscribed === false) {
      setBannerData({
        content: nonPlusContent[0]?.attributes,
        articleNotFound: false,
      })
    }
    if (plusContent?.length > 0 && users.currentlySubscribed === true) {
      setBannerData({
        content: plusContent[0]?.attributes,
        articleNotFound: false,
      })
    }
  }, [users.currentlySubscribed, strapiData, VIPUser])

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (loginChecked && !concurrencyCapKillswitch && users.subscriptionType === 'premier_sports' && users.gettingDetails) {
        // event.preventDefault()
        dispatch(checkConcurrentStream(true, channel, sportsExclusive, false))
        // eslint-disable-next-line no-param-reassign
        // event.returnValue = ''
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && loginChecked && !concurrencyCapKillswitch && users.subscriptionType === 'premier_sports' && users.gettingDetails) {
        dispatch(checkConcurrentStream(true, channel, sportsExclusive, false))
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('pagehide', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [loginChecked, concurrencyCapKillswitch, users, channel, sportsExclusive, dispatch])

  useEffect(() => {
    if (loginChecked && !concurrencyCapKillswitch && users.subscriptionType === 'premier_sports' && users.gettingDetails) {
      dispatch(checkConcurrentStream(false, channel, sportsExclusive, false))
    } else {
      dispatch(clearConcurrentStream({ loading: false }))
    }
  }, [loginChecked, dispatch, channel])

  if (groupToken === OUT_OF_REGION_LOGGED_IN_GROUPTOKEN && !psEnabled) {
    return (
      <div>
        <OutOfRegion />
      </div>
    )
  }

  return (
    <main className="Live">
      {renderError && errorRedirect()}

      {bannerData && (
        <div className="banner-wrapper">
          <a className="banner-link" href={bannerData?.content?.BannerLink}>
            {mobileView
              ? (
                <img
                  className="banner-image"
                  src={bannerData?.content?.MobileBannerImage?.data?.attributes?.url}
                  alt={bannerData?.content?.MobileBannerImage?.data?.attributes?.alternativeText}
                />
              )
              : (
                <img
                  className="banner-image"
                  src={bannerData?.content?.BannerImage?.data?.attributes?.url}
                  alt={bannerData?.content?.BannerImage?.data?.attributes?.alternativeText}
                />
              )}
          </a>
        </div>
      )}

      {streamMenuItems && (
        <ScrollerMenu menuItems={streamMenuItems} />
      )}

      {!renderError && (
        <>
          <div className="live-content-row">
            <div className="live-player-wrapper">

              {(!loginChecked || concurrentLoading) && (
                <div className="no-player">
                  <Spinner />
                </div>
              )}

              {showPlayerError && (
                <LoginDialog
                  center
                  currentDomain={currentDomain}
                  currentUrl={currentUrl}
                />
              )}
              {loginChecked && !showPlayerError && !concurrentLoading && !hasConcurrentStream && (!sportsExclusive || (sportsExclusive && users.subscriptionType === 'premier_sports')) && users.gettingDetails && (
                <LivePlayer
                  channel={channel}
                  channelTitle={menuTitle}
                  currentUrl={currentUrl}
                  currentDomain={currentDomain}
                  userAgent={userAgent}
                  programmeOrChannelGuid={programmeOrChannelGuid}
                  groupToken={groupToken}
                  loginEnabled={loginEnabled}
                />
              )}
              {loginChecked && !showPlayerError && !concurrentLoading && !hasConcurrentStream && sportsExclusive && users.subscriptionType !== 'premier_sports' && users.gettingDetails && (
                <div className="no-player">
                  <PaywallNotice logo={logo} />
                  <div className="concurrent-notice-bgimage-wrapper">
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    <img src={paywallBgImage} className="concurrent-notice-bgimage" />
                    <div className="concurrent-notice-bgimage-overlay" />
                  </div>
                </div>
              )}
              {/* eslint-disable-next-line max-len */}
              {loginChecked && !showPlayerError && !concurrentLoading && hasConcurrentStream && sportsExclusive && users.subscriptionType === 'premier_sports' && users.gettingDetails && (
                <div className="no-player">
                  <ConcurrentStreamNotice
                    renderMode="inline"
                    channel={channel}
                    sportsExclusive={sportsExclusive}
                  />
                  <div className="concurrent-notice-bgimage-wrapper">
                    <Image
                      src={require('./LivePage-nostreambg.png')}
                      alt="generic stream background image"
                      width={1261}
                      height={710}
                      className="concurrent-notice-bgimage"
                    />
                    <div className="concurrent-notice-bgimage-overlay" />
                  </div>
                </div>
              )}
            </div>
            {!onNowNextKillSwitch && hasSchedule && (
              <OnNowOnNext
                className="now-next"
                sportsExclusive={sportsExclusive}
                channel={channel}
                initialSchedule={scheduleAssets}
              />
            )}
          </div>
          <LiveTabs channel={channel} groupToken={groupToken} promoted={promoted} />
        </>
      )}
      <style jsx>{styles}</style>
    </main>
  )
}

Live.propTypes = {
  error: ERROR_OBJECT,
  channel: PropTypes.string.isRequired,
  currentUrl: PropTypes.string.isRequired,
  currentDomain: PropTypes.string.isRequired,
  userAgent: PropTypes.string.isRequired,
  programmeOrChannelGuid: PropTypes.string.isRequired,
  groupToken: PropTypes.string,
  streamMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      href: PropTypes.string,
      isActiveItem: PropTypes.bool,
      logoSrc: PropTypes.string,
    })
  ),
  loginEnabled: PropTypes.bool.isRequired,
  promoted: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  loginChecked: PropTypes.bool.isRequired,
  strapiData: PropTypes.object,
  VIPUser: PropTypes.bool.isRequired,
  menuTitle: PropTypes.string.isRequired,
  onNowNextKillSwitch: PropTypes.bool,
  hasSchedule: PropTypes.bool,
  logo: PropTypes.object,
  scheduleAssets: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    channel: PropTypes.string,
    summary: PropTypes.string,
    link: PropTypes.string,
    restartEnabled: PropTypes.bool,
    episode: PropTypes.object,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    id: PropTypes.string,
    image: PropTypes.object,
    modalImage: PropTypes.object,
  })),
  concurrencyCapKillswitch: PropTypes.bool,
  sportsExclusive: PropTypes.bool,
}

Live.defaultProps = {
  error: null,
  groupToken: null,
  streamMenuItems: null,
  promoted: { id: null, name: null },
  strapiData: {},
  onNowNextKillSwitch: false,
  concurrencyCapKillswitch: false,
  hasSchedule: false,
  scheduleAssets: null,
  logo: {},
  sportsExclusive: false,
}

export default Live
